import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { useLang, UI } from "@/i18n";
import { useContent } from "@/content";
import { Wordmark, FooterStars } from "@/components/Monogram";
import SocialLinks from "@/components/Social";

export default function Footer() {
  const { lang, t } = useLang();
  const { content } = useContent();
  const location = useLocation();
  const f = content && content.footer;
  const contacts = (content && content.contacts) || { email: "info@obsidianprivatedriver.it", pec: "obsidiantaxi@pec.it" };
  if (location.pathname.startsWith("/admin")) return null;

  const manageCookies = () => window.dispatchEvent(new Event("obsidian-manage-cookies"));
  const go = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="relative overflow-hidden border-t hairline" data-testid="footer">
      <FooterStars />
      <div className="pointer-events-none absolute -right-16 -bottom-24 opacity-[0.05]">
        <Wordmark size="lg" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <img src="/media/logo_white.png" alt="OBSIDIAN Private Driver" className="h-9 w-auto" data-testid="footer-brand-logo" />
            <p className="eyebrow mt-3 !text-[9px]">{f ? t(f.tagline) : "Private Driver"}</p>
            <p className="mt-6 max-w-xs text-[13px] font-light leading-relaxed text-[#A1A1AA]">{f ? t(f.service_line) : ""}</p>
            <p className="mt-8 text-[13px] font-light tracking-[0.28em] text-[#C5A059]" style={{ fontFamily: "var(--font-sans)" }}>Move privately. Move exclusively.</p>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow !text-[9px]">Menu</p>
            <ul className="mt-6 space-y-3">
              {[["experience", UI.nav.experience], ["servizi", UI.nav.services], ["viaggiare", UI.nav.vehicle], ["contatti", UI.nav.contacts]].map(([id, label]) => (
                <li key={id}>
                  <a href={`#${id}`} onClick={go(id)} className="text-[13px] font-light text-[#A1A1AA] transition-colors hover:text-[#FAF9F6]" data-testid={`footer-anchor-${id}`}>
                    {t(label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow !text-[9px]">Legal</p>
            <ul className="mt-6 space-y-3">
              <li><Link to="/termini" className="text-[13px] font-light text-[#A1A1AA] transition-colors hover:text-[#FAF9F6]" data-testid="footer-terms-link">{t(UI.footer.legal)}</Link></li>
              <li><Link to="/privacy" className="text-[13px] font-light text-[#A1A1AA] transition-colors hover:text-[#FAF9F6]" data-testid="footer-privacy-link">{t(UI.footer.privacy)}</Link></li>
              <li><Link to="/cookie" className="text-[13px] font-light text-[#A1A1AA] transition-colors hover:text-[#FAF9F6]" data-testid="footer-cookies-link">{t(UI.footer.cookies)}</Link></li>
              <li><button onClick={manageCookies} className="text-left text-[13px] font-light text-[#A1A1AA] transition-colors hover:text-[#FAF9F6]" data-testid="footer-manage-cookies">{t(UI.footer.manage)}</button></li>
              <li>
                <Link to="/admin" className="inline-flex items-center gap-2 text-[13px] font-light text-[#FAF9F6]/70 transition-colors hover:text-[#C5A059]" data-testid="footer-admin-link-2">
                  <Lock size={12} strokeWidth={1.5} /> {t(UI.footer.reserved)}
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow !text-[9px]">Contatti</p>
            <ul className="mt-6 space-y-3 text-[13px] font-light text-[#A1A1AA]">
              <li><a href={`mailto:${contacts.email}`} className="transition-colors hover:text-[#FAF9F6]" data-testid="footer-email">{contacts.email}</a></li>
              <li><a href="tel:+393751163111" className="transition-colors hover:text-[#FAF9F6]" data-testid="footer-phone">+39 375 116 3111 · Nicky</a></li>
              <li>PEC: {contacts.pec}</li>
            </ul>
            <div className="mt-6">
              <SocialLinks />
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-14 gap-y-6 border-t hairline pt-10 lg:justify-between" data-testid="footer-partners">
          <img src="/media/logo_yesalps.png" alt="YesAlps" className="h-8 w-auto" loading="lazy" data-testid="partner-yesalps" />
          <img src="/media/partner_alpelusia_white.png" alt="Alpe Lusia — Moena · Bellamonte" className="h-[23px] w-auto" loading="lazy" data-testid="partner-alpelusia" />
          <img src="/media/partner_skiarea_white.png" alt="Sanpellegrino Skiarea" className="h-[23px] w-auto" loading="lazy" data-testid="partner-skiarea" />
          <img src="/media/partner_trentino_white.png" alt="Trentino" className="h-[30px] w-auto" loading="lazy" data-testid="partner-trentino" />
          <img src="/media/partner_moena_white.png" alt="Moena.it — La fata delle Dolomiti" className="h-14 w-auto" loading="lazy" data-testid="partner-moena" />
          <img src="/media/partner_qcterme_white.png" alt="QC Terme — Spa of Wonders" className="h-9 w-auto" loading="lazy" data-testid="partner-qcterme" />
        </div>

        <div className="mt-16 flex flex-col gap-6 border-t hairline pt-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-end gap-6">
            <img
              src="/media/valdifassa.png"
              alt="Val di Fassa"
              className="h-16 w-auto"
              loading="lazy"
              data-testid="valdifassa-logo"
            />
            <div className="max-w-xl text-[11px] font-light leading-relaxed text-[#FAF9F6]/40">
              {f && <p>{f.company} · {f.address} · {f.piva}</p>}
              <p className="mt-2">© {new Date().getFullYear()} {f ? f.copyright : ""}</p>
            <p className="mt-1 text-[10px] tracking-[0.15em] uppercase">Website & brand made by Agnese — Ubuntu Strategy Srls</p>
            </div>
          </div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 border hairline px-5 py-3 text-[11px] uppercase tracking-[0.25em] text-[#FAF9F6]/70 transition-colors hover:border-[#C5A059]/60 hover:text-[#C5A059]"
            data-testid="footer-admin-link"
          >
            <Lock size={12} strokeWidth={1.5} /> {t(UI.footer.reserved)}
          </Link>
        </div>
      </div>
    </footer>
  );
}
